/**
 * Unified API Client
 * Base axios client with shared interceptors for authentication and error handling
 */

import axios from 'axios'
import { isSwitchedOffRefusal, noteModuleRefusal } from './moduleRefusal'
import { AUTH_CONFIG, API_ERRORS } from './config'
import { flattenDetail } from './failure'
// Token freshness + /auth/refresh single-flight are shared app-wide (see
// tokenRefresh.js) so this client and src/api/index.js never fire independent,
// competing refreshes with the same rotating refresh token.
import { ensureFreshToken, decodeTokenPayload } from './tokenRefresh'
import { markApiActivity } from './sessionPolicy'
// Phase B3: the access token lives in memory (tokenStore), never in localStorage.
import { getAccessToken, setAccessToken, clearAccessToken, sessionKeptThroughBusyMinute } from './tokenStore'

/**
 * Create an API client for a specific service
 * @param {string} baseURL - Base URL for the service
 * @param {number} timeout - Request timeout in milliseconds
 * @returns {AxiosInstance} Configured axios instance
 */
export const createAPIClient = (baseURL, timeout = 10000) => {
  const client = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor - Add authentication token (with proactive refresh)
  client.interceptors.request.use(
    async (config) => {
      const token = await ensureFreshToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // A request that CHANGES something is somebody pressing something, and until now nothing
      // an API did counted as being here at all — a twenty-minute upload or a long report left a
      // person looking idle. Reads are deliberately excluded: this app runs background pollers,
      // and if a GET counted, a dashboard left open would hold a session open forever.
      markApiActivity(config)
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor - Handle common errors
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const { response, code } = error

      // Network error (no response)
      if (!response) {
        if (code === 'ECONNABORTED') {
          error.userMessage = API_ERRORS.TIMEOUT
        } else {
          error.userMessage = API_ERRORS.NETWORK_ERROR
        }
        return Promise.reject(error)
      }

      // Handle HTTP errors
      const { status, data } = response

      switch (status) {
        case 401: {
          // Only clear auth and redirect if the local token is actually expired or absent.
          // A 401 from a backend service (e.g. bidworkup notifications) when the identity
          // token is still valid means that service has an auth issue — NOT that the user's
          // session is expired. Blindly logging out in that case is incorrect.
          // Phase B3: the access token is in memory, not localStorage.
          const storedToken = getAccessToken()
          const payload = decodeTokenPayload(storedToken)
          const nowSec = Math.floor(Date.now() / 1000)
          const tokenExpired = !storedToken || !payload || (payload.exp && payload.exp <= nowSec)

          if (tokenExpired && sessionKeptThroughBusyMinute()) {
            // This request went out with NO token because the refresh just before it met a 429, a
            // restart or a dropped connection - not because the server refused the session. Signing
            // the person out here is what sent people to /login in a busy minute. Leave the session
            // alone: the next request refreshes again, and a refusal that really ends it (401/403 on
            // /auth/refresh) clears the stored user in tokenStore itself.
            console.warn('401 on a request sent while the sign-in service was busy - keeping the session')
            error.userMessage = 'The server is busy. Please try again in a moment.'
            // failure.js reads this, so a screen says "busy, try again" with a Retry button rather
            // than "your session has expired" to somebody who is still signed in.
            error.sessionKept = true
            return Promise.reject(error)
          } else if (tokenExpired) {
            console.warn('401 Unauthorized - Token expired, clearing authentication')
            clearAccessToken()
            localStorage.removeItem(AUTH_CONFIG.USER_KEY)
            localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_KEY)

            // Only redirect if not already on login/reset pages
            const currentPath = window.location.pathname
            if (currentPath !== '/login' && !currentPath.includes('/reset-password')) {
              setTimeout(() => {
                window.location.href = '/login'
              }, 100)
            }
          } else {
            console.warn('401 from service but local token is valid — ignoring (service auth issue, not session expiry)')
          }

          error.userMessage = API_ERRORS.UNAUTHORIZED
          break
        }

        case 403:
          // A module switched off for this company says so in its own words; say them, and have the
          // app re-read its module switches (shared/api/moduleRefusal.js).
          error.userMessage = isSwitchedOffRefusal(status, data) ? data.detail : API_ERRORS.FORBIDDEN
          noteModuleRefusal(status, data)
          break

        case 404:
          error.userMessage = API_ERRORS.NOT_FOUND
          noteModuleRefusal(status, data)
          break

        case 500:
        case 502:
        case 503:
        case 504:
          error.userMessage = API_ERRORS.SERVER_ERROR
          break

        case 429:
          // Identity's per-address limit (an office shares one address). "Something went wrong" is
          // false; the server is only busy, and failure.js already offers Retry for it.
          error.userMessage = 'The server is busy. Please try again in a moment.'
          break

        case 409:
          // Used to fall through to the default and, with no `detail`, render as SERVER_ERROR —
          // "something broke" for what is actually "somebody else changed this first".
          error.userMessage = flattenDetail(data?.detail) || API_ERRORS.CONFLICT
          break

        default:
          // Use the backend error message if available — FLATTENED. A 422's `detail` is an ARRAY of
          // objects, and assigning it raw (which is what this line did) put an object on
          // `userMessage`. `getErrorMessage` hands that straight to JSX at 41 call sites, where it
          // is React error #31 — "objects are not valid as a React child" — a blank screen produced
          // by the error path itself.
          error.userMessage = flattenDetail(data?.detail) || data?.message || API_ERRORS.SERVER_ERROR
      }

      return Promise.reject(error)
    }
  )

  return client
}

/**
 * Helper function to extract error message for display
 * @param {Error} error - Axios error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  // Guaranteed to return a STRING. The precedence is unchanged — this is the house dialect at 41
  // call sites and several render it directly — but `userMessage` is no longer trusted to be a
  // string: an error from a client that was built without the interceptor above can carry a raw
  // FastAPI 422 `detail` array, and returning that hands an object to JSX.
  const carried = error?.userMessage
  if (typeof carried === 'string' && carried) return carried
  if (carried) {
    const flat = flattenDetail(carried)
    if (flat) return flat
  }
  return error?.message || API_ERRORS.SERVER_ERROR
}

/**
 * Helper function to check if user is authenticated
 * @returns {boolean} True if user has valid token
 */
export const isAuthenticated = () => {
  // Phase B3 LANDMINE: this used to be "is there an access token in localStorage?". The access
  // token now lives in memory, so on every page reload that test is false for a perfectly valid
  // session and the app would bounce the user to /login before bootstrap() could mint a token
  // from the refresh cookie. Derive session state from the persisted user object instead —
  // bootstrap() clears it when the refresh cookie turns out to be invalid, so a stale user
  // cannot outlive the session.
  return !!localStorage.getItem(AUTH_CONFIG.USER_KEY)
}

/**
 * Helper function to get current user from storage
 * @returns {object|null} User object or null
 */
export const getCurrentUser = () => {
  const userStr = localStorage.getItem(AUTH_CONFIG.USER_KEY)
  if (!userStr) return null
  
  try {
    return JSON.parse(userStr)
  } catch (e) {
    console.error('Failed to parse user data:', e)
    return null
  }
}

/**
 * Helper function to clear authentication data
 */
export const clearAuth = () => {
  clearAccessToken()
  localStorage.removeItem(AUTH_CONFIG.USER_KEY)
  localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_KEY)
  // Purge any pre-B3 token left in storage by an older bundle.
  localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY)
}

/**
 * Helper function to save authentication data
 * @param {string} token - Access token
 * @param {object} user - User object
 */
export const saveAuth = (token, user) => {
  setAccessToken(token)
  localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(user))
}
